import { Button } from '@timelyapp/react';
import classNames from 'classnames';
import IntersectionObserver from 'components/IntersectionObserver';
import type { Props as PopoverProps } from 'components/popovers/Popover';
import Popover from 'components/popovers/Popover';
import type { HintKey } from 'constants/hints';
import { HINT_META_DATA } from 'constants/hints';
import React from 'react';
import type { AllAcceptedUserPropertyKey, UserPropertyValue } from 'redux/modules/user.types';
import { TrackingActions } from 'tracking/actions';
import type { Merge } from 'types/utility';
import styles from './OnboardingHintPopover.module.scss';

interface OwnProps {
  /** This will be called when the Confirm button is clicked */
  onConfirm: (property: AllAcceptedUserPropertyKey, value?: UserPropertyValue) => void;

  /** Label text for the Confirm <Button /> */
  confirmLabel?: string;
  /** The key of the hint that's displayed. This maps to `HINT_META_DATA` in `constants/user` */
  hintKey: HintKey;
  children?: React.ReactElement;
  noDelay?: boolean;
  hideIfNotVisible?: boolean;
}
type Props = Merge<PopoverProps, OwnProps>;

interface State {
  isDelayOver: boolean;
  isVisible: boolean;
}

class OnboardingHintPopover extends React.Component<Props, State> {
  delayTimerId: number | undefined;

  state = {
    isDelayOver: this.props.noDelay ?? false,
    isVisible: !this.props.hideIfNotVisible,
  };

  componentDidMount() {
    const delay = this.getDelay();
    const hint = this.getHint();
    if (!delay || !hint) {
      return;
    }
    this.delayTimerId = window.setTimeout(() => this.setState({ isDelayOver: true }), delay);
    const { hintKey } = this.props;
    TrackingActions.viewedHint(hintKey, `${hint.name}: ${hint.description}`);
  }

  componentWillUnmount() {
    if (this.delayTimerId) {
      clearTimeout(this.delayTimerId);
    }
  }

  getHint = () => {
    const { hintKey } = this.props;
    const hint = HINT_META_DATA[hintKey];
    return hint;
  };

  getDelay = () => {
    const hint = this.getHint();
    const delay = hint && hint.delay;
    return delay;
  };

  isReadyToBeShown = () => {
    if (this.props.hideIfNotVisible && !this.state.isVisible) {
      return false;
    }

    const delay = this.getDelay();
    return !delay || (!!delay && this.state.isDelayOver);
  };

  handleConfirm = (event: React.SyntheticEvent) => {
    // In case we're in a form, ref EventEditorAttachments
    event.preventDefault();
    this.props.onConfirm(this.props.hintKey);
  };

  handleOnIntersectingChange = (isVisible: boolean) => {
    this.setState({ isVisible });
  };

  render() {
    const { className, target, children, confirmLabel, hintKey, isOpen, ...rest } = this.props;
    const targetwithFallback = target || children || undefined;

    const hint = this.getHint();

    const popoverProps: PopoverProps = {
      enableKeyTrap: false, // @todo This should be enabled, but need better focus/keytrap management first.
      ...rest,
      isOpen: isOpen && this.isReadyToBeShown(),
      target: targetwithFallback,
    };

    // Render props.target or children if hint is not showing.
    if (!hint || !isOpen) {
      return targetwithFallback;
    }

    const containerClass = classNames(styles.container, className);

    const label: string = confirmLabel || 'Ok, got it!';

    return (
      <IntersectionObserver onIntersectingChange={this.handleOnIntersectingChange}>
        <Popover {...popoverProps}>
          <div data-testid="onboarding_hint_popover_container" className={containerClass}>
            <div>
              <h5>{hint.name}</h5>
              <p>{hint.description}</p>
            </div>
            <div className={styles.buttons} data-testid="onboarding_hint_popover_button">
              <Button size="small" onClick={this.handleConfirm}>
                {label}
              </Button>
            </div>
          </div>
        </Popover>
      </IntersectionObserver>
    );
  }
}

export default OnboardingHintPopover;
